import type { SeedingDetailProduct, SeedingItem } from '@/app/(activity)/campaigns/[slug]/types';
import type { JumpTarget } from '@/components/MagicJump';

import type { MaterialGallery2026TrackingParams, MaterialGalleryItem } from './types';

export const MAX_MATERIAL_COUNT = 8;

export const normalizeConfiguredText = (text?: unknown) => {
  if (typeof text !== 'string') return '';

  return text.replace(/<br\s*\/?>/gi, '\n').trim();
};

const isImageMaterial = (mediaType?: string | null) => {
  const normalizedType = mediaType?.trim().toLowerCase();
  return !normalizedType || normalizedType === 'img' || normalizedType === 'image';
};

export function getValidMaterialItems(items?: MaterialGalleryItem[]) {
  return (items ?? [])
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(({ item }) => {
      const itemId = Number(item.id);
      const validMaterialCount = Number(item.valid_material_count);

      return (
        Number.isFinite(itemId) &&
        itemId > 0 &&
        Boolean(item.jump_image?.trim()) &&
        Number.isFinite(validMaterialCount) &&
        validMaterialCount > 0 &&
        isImageMaterial(item.media_type)
      );
    })
    .slice(0, MAX_MATERIAL_COUNT);
}

export function getMaterialShopSkus(items: ReturnType<typeof getValidMaterialItems>) {
  const shopSkus = new Set<string>();

  for (const { item } of items) {
    const shopSku = item.shop_sku?.trim();
    if (shopSku) shopSkus.add(shopSku);
  }

  return Array.from(shopSkus);
}

export function buildSeedingSummaryMap(items?: SeedingItem[]) {
  const summaryMap = new Map<number, SeedingItem>();

  for (const item of items ?? []) {
    const itemId = Number(item.item_id ?? item.id);
    if (Number.isFinite(itemId) && itemId > 0) {
      summaryMap.set(itemId, item);
    }
  }

  return summaryMap;
}

export function buildMaterialTrackingParams({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  product,
}: {
  item: MaterialGalleryItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  product?: SeedingDetailProduct;
}): MaterialGallery2026TrackingParams {
  return {
    module_index: moduleIndex,
    module_name: componentId,
    module_id: moduleId,
    module_title: moduleTitle,
    mkt_index: originalIndex,
    mkt_index_sub: '',
    mkt_jump_type: '2',
    mkt_jump_content: item.shop_sku || product?.shop_sku || '',
    module_tab_name: '',
    coupon_id_list: '',
    coupon_name_list: '',
    shop_sku: item.shop_sku || product?.shop_sku || '',
    business_sku: product?.business_sku || '',
    product_sku: product?.sku || '',
    product_id: String(product?.id || ''),
    product_name: product?.short_name || product?.name || '',
    group_id: '',
    group_name: '',
  };
}

export function buildGalleryPageTrackingParams({
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  jumpTarget,
}: {
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  jumpTarget: JumpTarget;
}): MaterialGallery2026TrackingParams {
  return {
    module_index: moduleIndex,
    module_name: componentId,
    module_id: moduleId,
    module_title: moduleTitle,
    mkt_index: 0,
    mkt_index_sub: 'page',
    mkt_jump_type: String(jumpTarget.jump_type || ''),
    mkt_jump_content: String(jumpTarget.Jump_target || ''),
    module_tab_name: '',
    coupon_id_list: '',
    coupon_name_list: '',
    shop_sku: '',
    business_sku: '',
    product_sku: '',
    product_id: '',
    product_name: '',
    group_id: String(jumpTarget.product_group?.id || ''),
    group_name: jumpTarget.product_group?.name || '',
  };
}
